const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

function hasSupabaseConfig() {
  return Boolean(SUPABASE_URL && SUPABASE_KEY);
}

async function apiGet(path) {
  const response = await fetch(`${SUPABASE_URL}${path}`, {
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`
    }
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Supabase request failed: ${response.status} ${message}`);
  }

  return response.json();
}

function mapLessonType(type) {
  if (type === 'recap') return 'recap';
  if (type === 'quiz' || type === 'exam_standard') return 'quiz';
  if (type === 'exam_captain') return 'mindset';
  return 'story';
}

function mapLessonSection(type, language) {
  if (type === 'recap' || type === 'quiz' || type === 'exam_standard' || type === 'exam_captain') {
    return language === 'ro' ? 'Final de Modul' : 'End of Module';
  }
  return language === 'ro' ? 'Conținut Lecție' : 'Lesson Content';
}

export async function fetchBeginnerModules(language) {
  if (!hasSupabaseConfig()) {
    return null;
  }

  const levels = await apiGet(
    "/rest/v1/levels?slug=eq.beginner&select=id,slug,title_ro,title_en,description_ro,description_en"
  );
  const beginnerLevel = levels[0];
  if (!beginnerLevel) {
    return null;
  }

  const modules = await apiGet(
    `/rest/v1/modules?level_id=eq.${beginnerLevel.id}` +
      `&order=sort_order.asc&select=id,slug,title_ro,title_en,description_ro,description_en,sort_order,is_published`
  );
  const academyModulesOnly = (modules || []).filter(
    (module) => !String(module?.slug || '').toLowerCase().startsWith('b-o')
  );

  if (!academyModulesOnly.length) {
    return [];
  }

  const moduleIds = academyModulesOnly.map((module) => module.id).join(",");
  let lessons = [];
  try {
    lessons = await apiGet(
      `/rest/v1/lessons?module_id=in.(${moduleIds})` +
        `&order=sort_order.asc&select=id,module_id,slug,title_ro,title_en,lesson_type,duration_min,sort_order,difficulty,is_published`
    );
  } catch {
    // Backward compatible: difficulty column may not exist yet.
    lessons = await apiGet(
      `/rest/v1/lessons?module_id=in.(${moduleIds})` +
        `&order=sort_order.asc&select=id,module_id,slug,title_ro,title_en,lesson_type,duration_min,sort_order,is_published`
    );
  }

  return academyModulesOnly.map((module) => {
    const moduleSortOrder = Number(module.sort_order || 0);
    const moduleIsPublished = Boolean(module.is_published);
    const moduleLessons = lessons
      .filter((lesson) => lesson.module_id === module.id)
      .map((lesson) => ({
        id: lesson.slug || lesson.id,
        dbId: lesson.id,
        section: mapLessonSection(lesson.lesson_type, language),
        type: mapLessonType(lesson.lesson_type),
        title: language === "ro" ? lesson.title_ro : lesson.title_en,
        duration: lesson.duration_min ? `~${lesson.duration_min} min` : "~5 min",
        sortOrder: Number(lesson.sort_order || 0),
        difficulty: lesson.difficulty || 'easy',
        // Availability from DB publishing state. Access by plan is handled in AcademyHome gating.
        available: moduleIsPublished && Boolean(lesson.is_published)
      }));

    return {
      id: module.slug || module.id,
      sortOrder: moduleSortOrder || 0,
      title: language === "ro" ? module.title_ro : module.title_en,
      summary: language === "ro" ? module.description_ro : module.description_en,
      progress: 0,
      accent: "#1e40af",
      available: moduleIsPublished,
      avatarTip:
        language === "ro"
          ? "Chessy, partenera ta de învățat: Hai să începem aventura împreună."
          : "Chessy, your learning buddy: Let's start the adventure together.",
      lessons: moduleLessons
    };
  });
}

export async function fetchOpeningsModules(language) {
  if (!hasSupabaseConfig()) {
    return null;
  }

  // Opening modules follow the slug convention b-o*
  const modules = await apiGet(
    `/rest/v1/modules?slug=like.b-o*` +
      `&order=sort_order.asc&select=id,slug,title_ro,title_en,description_ro,description_en,sort_order,is_published`
  );
  if (!modules.length) {
    return [];
  }

  const moduleIds = modules.map((module) => module.id).join(",");
  let lessons = [];
  try {
    lessons = await apiGet(
      `/rest/v1/lessons?module_id=in.(${moduleIds})` +
        `&order=sort_order.asc&select=id,module_id,slug,title_ro,title_en,lesson_type,duration_min,sort_order,difficulty,is_published`
    );
  } catch {
    lessons = await apiGet(
      `/rest/v1/lessons?module_id=in.(${moduleIds})` +
        `&order=sort_order.asc&select=id,module_id,slug,title_ro,title_en,lesson_type,duration_min,sort_order,is_published`
    );
  }

  return modules.map((module) => {
    const moduleSortOrder = Number(module.sort_order || 0);
    const moduleIsPublished = Boolean(module.is_published);
    const moduleLessons = lessons
      .filter((lesson) => lesson.module_id === module.id)
      .map((lesson) => ({
        id: lesson.slug || lesson.id,
        dbId: lesson.id,
        section: mapLessonSection(lesson.lesson_type, language),
        type: mapLessonType(lesson.lesson_type),
        title: language === 'ro' ? lesson.title_ro : lesson.title_en,
        duration: lesson.duration_min ? `~${lesson.duration_min} min` : '~5 min',
        sortOrder: Number(lesson.sort_order || 0),
        difficulty: lesson.difficulty || 'easy',
        available: moduleIsPublished && Boolean(lesson.is_published)
      }));

    return {
      id: module.slug || module.id,
      sortOrder: moduleSortOrder || 0,
      title: language === 'ro' ? module.title_ro : module.title_en,
      summary: language === 'ro' ? module.description_ro : module.description_en,
      progress: 0,
      accent: '#1e40af',
      available: moduleIsPublished,
      avatarTip:
        language === 'ro'
          ? 'Planurile bune încep cu o deschidere clară. Jucăm principii, nu memorare oarbă.'
          : 'Good plans start with a clear opening. We play principles, not blind memorization.',
      lessons: moduleLessons
    };
  });
}

export function isSupabaseConfigured() {
  return hasSupabaseConfig();
}
